import { Link } from "react-router-dom";
import { Palette, Clock, ArrowLeft, Download, Printer, Star, Heart, Sparkles, CheckCircle, HelpCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Helmet } from "react-helmet-async";

const ArticlePawPatrolSkye = () => {
  // PDF URL for the printable coloring pages
  const pdfUrl = "https://customer-assets.emergentagent.com/job_7af6b513-b0fc-403e-8926-9a59fd92ae5b/artifacts/vif5inu6_colormagic_skye_paw_patrol.pdf";

  const coloringPages = [
    {
      title: "Skye Portrait",
      difficulty: "Easy",
      ageRange: "3-5",
      description: "A simple portrait of Skye sitting with her pilot goggles on her head. Perfect for little ones just learning to color!",
      image: "/blog/paw-patrol-skye/skye_page_1.jpg"
    },
    {
      title: "Skye in Action",
      difficulty: "Easy",
      ageRange: "3-5", 
      description: "Skye in a fun flying pose with her front paws extended. Great for kids who love action scenes!",
      image: "/blog/paw-patrol-skye/skye_page_2.jpg"
    },
    {
      title: "Skye with Friends",
      difficulty: "Moderate",
      ageRange: "5-7",
      description: "Skye surrounded by Marshall and Chase with hearts, clouds, and flowers. More details for older kids!",
      image: "/blog/paw-patrol-skye/skye_page_3.jpg"
    },
    {
      title: "Skye at the Lookout",
      difficulty: "Moderate",
      ageRange: "5-7",
      description: "Skye in an outdoor scene with grass, flowers, and the PAW Patrol tower in the background.",
      image: "/blog/paw-patrol-skye/skye_page_4.jpg"
    },
    {
      title: "Skye Close-Up",
      difficulty: "Easy",
      ageRange: "3-5",
      description: "A cute close-up of Skye's face decorated with stars, hearts, and flowers around her.",
      image: "/blog/paw-patrol-skye/skye_page_5.jpg"
    },
    {
      title: "Skye in the Meadow",
      difficulty: "Moderate",
      ageRange: "5-7",
      description: "Skye sitting in a beautiful meadow with flowers, clouds, and a smiling sun.",
      image: "/blog/paw-patrol-skye/skye_page_6.jpg"
    },
    {
      title: "Skye's Adventure Gear",
      difficulty: "Moderate",
      ageRange: "6-7",
      description: "Skye surrounded by her helicopter, pup tag, shield, and other adventure gear!",
      image: "/blog/paw-patrol-skye/skye_page_7.jpg"
    }
  ];

  const benefits = [
    {
      title: "Fine Motor Skills",
      description: "Coloring helps develop the small muscles in hands and fingers needed for writing"
    },
    {
      title: "Color Recognition",
      description: "Kids learn to identify and choose colors, building their visual vocabulary"
    },
    {
      title: "Focus & Patience",
      description: "Staying within lines teaches concentration and patience in a fun way"
    },
    {
      title: "Self-Expression",
      description: "There's no wrong way to color! Kids express creativity through color choices"
    }
  ];

  const faqs = [
    {
      question: "What age are these Skye coloring pages best for?",
      answer: "Our Skye coloring pages are designed for kids ages 3-7. We've included simpler pages with larger areas for preschoolers (3-5) and more detailed pages for older kids (6-7) who want a bit more challenge."
    },
    {
      question: "Can I print these coloring pages for free?",
      answer: "Yes! Click the download button to get the PDF with all 7 Skye coloring pages. You can print them at home on regular printer paper. For best results, use white cardstock."
    },
    {
      question: "What colors should I use for Skye?",
      answer: "Skye is a Cockapoo with cream/tan colored fur and pink accents on her outfit. Her pilot goggles have pink frames. But remember - it's coloring! Your child can make Skye any color they want!"
    },
    {
      question: "Do you have other PAW Patrol characters?",
      answer: "Yes! ColorWonder offers coloring pages for all your favorite PAW Patrol pups including Chase, Marshall, Rubble, Rocky, Zuma, and Everest. Check our generator to create custom PAW Patrol scenes!"
    },
    {
      question: "Can I use these for a PAW Patrol birthday party?",
      answer: "Absolutely! These coloring pages make perfect party activities. Print multiple copies and set up a coloring station. Kids love it, and parents appreciate the quiet activity time!"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Skye PAW Patrol Coloring Pages - Free Printables for Kids | ColorWonder</title>
        <meta name="description" content="This pup's gotta fly! Free Skye coloring pages perfect for preschoolers. Print instantly from ColorWonder.app - 7 adorable pages included!" />
        <meta name="keywords" content="skye paw patrol coloring page, paw patrol coloring pages, skye coloring sheet, free printable coloring pages, preschool coloring pages" />
        <link rel="canonical" href="https://colorwonder.app/blog/paw-patrol-skye-coloring-pages" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Skye PAW Patrol Coloring Pages - Free Printables for Kids" />
        <meta property="og:description" content="This pup's gotta fly! Free Skye coloring pages perfect for preschoolers. 7 adorable printable pages!" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://colorwonder.app/blog/paw-patrol-skye-coloring-pages" />
        
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Skye PAW Patrol Coloring Pages - Free Fun for Little Pups",
            "description": "Free printable Skye coloring pages from PAW Patrol. Perfect for kids ages 3-7 with easy and moderate difficulty levels.",
            "author": {
              "@type": "Organization",
              "name": "ColorWonder"
            },
            "publisher": {
              "@type": "Organization",
              "name": "ColorWonder",
              "logo": {
                "@type": "ImageObject",
                "url": "https://colorwonder.app/logo.png"
              }
            },
            "datePublished": "2025-02-04",
            "dateModified": "2025-02-15",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://colorwonder.app/blog/paw-patrol-skye-coloring-pages"
            },
            "image": "https://colorwonder.app/blog/paw-patrol-skye/skye_page_1.jpg"
          })}
        </script>
        {/* Fallback H1 for crawlers that don't execute JavaScript */}
        <noscript>
          {`<style>.js-h1{display:none}</style>`}
        </noscript>
      </Helmet>

      <div className="min-h-screen bg-hero-gradient">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                  <Palette className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                  ColorWonder
                </span>
              </Link>

              <div className="flex items-center gap-4">
                <Link to="/blog" className="text-slate-600 hover:text-slate-900 font-medium">
                  Blog
                </Link>
                <Button
                  onClick={() => window.location.href = "/signup"}
                  className="h-10 px-6 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold"
                >
                  Try Free
                </Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Article Content */}
        <article 
          className="pt-24 pb-16 px-6 md:px-12 lg:px-24"
          itemScope 
          itemType="https://schema.org/Article"
        >
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            {/* Header */}
            <header className="mb-12">
              <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
                <span className="px-3 py-1 bg-pink-100 text-pink-700 rounded-full font-medium">Free Printables</span>
                <span>•</span>
                <time itemProp="datePublished" dateTime="2025-02-15">Feb 2025</time>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  5 min read
                </span>
              </div>
              
              <h1 
                className="text-4xl md:text-5xl font-bold text-slate-800 mb-6" 
                style={{ fontFamily: "'Fredoka', sans-serif" }}
                data-testid="article-h1"
                itemProp="headline"
              >
                Skye PAW Patrol Coloring Pages - Free Fun for Little Pups
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed">
                This pup&apos;s gotta fly! Download 7 free Skye coloring pages perfect for your little PAW Patrol fan. From simple portraits for preschoolers to detailed adventure scenes for bigger kids!
              </p>
            </header>

            {/* Download CTA */}
            <div className="bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl p-6 mb-12 text-white">
              <div className="flex items-center gap-2 mb-3">
                <Download className="w-5 h-5" />
                <span className="font-semibold">Download All 7 Pages Free!</span>
              </div>
              <p className="text-pink-100 mb-4">
                Get instant access to all Skye coloring pages in one easy PDF. Just click, download, and print!
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => window.open(pdfUrl, '_blank')}
                  className="h-10 px-6 rounded-full bg-white text-pink-600 font-semibold hover:bg-pink-50"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button
                  onClick={() => {
                    const printWindow = window.open(pdfUrl, '_blank');
                    printWindow.onload = () => printWindow.print();
                  }}
                  variant="outline"
                  className="h-10 px-6 rounded-full border-white text-white font-semibold hover:bg-white/10"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Now
                </Button>
              </div>
            </div>

            {/* Table of Contents */}
            <div className="bg-slate-50 rounded-2xl p-6 mb-12">
              <h2 className="font-semibold text-slate-800 mb-4">📋 What&apos;s Inside</h2>
              <ul className="space-y-2 text-slate-600">
                <li><a href="#who-is-skye" className="hover:text-violet-600">Who is Skye?</a></li>
                <li><a href="#easy-pages" className="hover:text-violet-600">Easy Coloring Pages (Ages 3-5)</a></li>
                <li><a href="#moderate-pages" className="hover:text-violet-600">Moderate Pages (Ages 6-7)</a></li>
                <li><a href="#benefits" className="hover:text-violet-600">Benefits of Coloring for Kids</a></li>
                <li><a href="#more-characters" className="hover:text-violet-600">More PAW Patrol Characters</a></li>
                <li><a href="#faq" className="hover:text-violet-600">Parent FAQ</a></li>
              </ul>
            </div>

            {/* Who is Skye */}
            <section id="who-is-skye" className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Who is Skye? 🐕‍🦺
              </h2>
              <div className="bg-pink-50 border border-pink-200 rounded-xl p-6 mb-6">
                <p className="text-slate-700 mb-4">
                  <strong>Skye</strong> is the brave and fearless pilot pup of the PAW Patrol team! She&apos;s a lovable Cockapoo (that&apos;s a Cocker Spaniel and Poodle mix) who loves to take to the skies in her helicopter.
                </p>
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-pink-500 mt-1 flex-shrink-0" />
                    <span><strong>Catchphrase:</strong> &ldquo;This pup&apos;s gotta fly!&rdquo;</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-pink-500 mt-1 flex-shrink-0" />
                    <span><strong>Vehicle:</strong> A pink helicopter that transforms into different modes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-pink-500 mt-1 flex-shrink-0" />
                    <span><strong>Skills:</strong> Flying, aerial rescues, and being super brave!</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Star className="w-4 h-4 text-pink-500 mt-1 flex-shrink-0" />
                    <span><strong>Colors:</strong> Cream/tan fur with pink pilot outfit and goggles</span>
                  </li>
                </ul>
              </div>
              <p className="text-slate-600">
                Skye shows kids that girls can do anything - including flying helicopters and saving the day! She&apos;s confident, caring, and always ready to help her friends. No wonder she&apos;s one of the most popular PAW Patrol pups!
              </p>
            </section>

            {/* Easy Pages */}
            <section id="easy-pages" className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Easy Coloring Pages (Ages 3-5) ⭐
              </h2>
              <p className="text-slate-600 mb-6">
                These pages feature larger coloring areas and simpler designs - perfect for little hands just learning to hold crayons!
              </p>
              
              <div className="grid md:grid-cols-3 gap-4">
                {coloringPages.filter(p => p.difficulty === "Easy").map((page, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="aspect-[3/4] bg-slate-50 relative">
                      <img 
                        src={page.image} 
                        alt={page.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <span className="absolute top-2 right-2 px-2 py-1 bg-green-500 text-white text-xs rounded-full font-medium">
                        Ages {page.ageRange}
                      </span>
                    </div>
                    <div className="p-4">
                      <h4 className="font-semibold text-slate-800 mb-1">{page.title}</h4>
                      <p className="text-slate-600 text-sm line-clamp-2">{page.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-slate-700">
                  <strong>💡 Tip for parents:</strong> Start with chunky crayons or markers for toddlers. They&apos;re easier to grip and won&apos;t break as easily when those little hands press too hard!
                </p>
              </div>
            </section>

            {/* Moderate Pages */}
            <section id="moderate-pages" className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Moderate Coloring Pages (Ages 6-7) ⭐⭐
              </h2>
              <p className="text-slate-600 mb-6">
                Ready for more detail? These pages have smaller areas and more intricate designs for kids who&apos;ve mastered the basics!
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                {coloringPages.filter(p => p.difficulty === "Moderate").map((page, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow flex">
                    <div className="w-1/3 bg-slate-50 relative">
                      <img 
                        src={page.image} 
                        alt={page.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="w-2/3 p-4 flex flex-col justify-center">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-slate-800">{page.title}</h4>
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                          Ages {page.ageRange}
                        </span>
                      </div>
                      <p className="text-slate-600 text-sm">{page.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-purple-50 border border-purple-200 rounded-xl p-4">
                <p className="text-slate-700">
                  <strong>💡 Tip for parents:</strong> Colored pencils work great for detailed pages! They allow for more precise coloring and kids can practice light vs. heavy pressure for shading effects.
                </p>
              </div>
            </section>

            {/* Benefits */}
            <section id="benefits" className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Why Coloring is Great for Kids 🎨
              </h2>
              <p className="text-slate-600 mb-6">
                Coloring isn&apos;t just fun - it&apos;s actually helping your child develop important skills!
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                {benefits.map((benefit, i) => (
                  <div key={i} className="bg-gradient-to-br from-violet-50 to-pink-50 rounded-xl p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-violet-600" />
                      <h4 className="font-semibold text-slate-800">{benefit.title}</h4>
                    </div>
                    <p className="text-slate-600 text-sm">{benefit.description}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-slate-100 rounded-xl p-4">
                <p className="text-slate-700 text-sm">
                  <strong>Research shows:</strong> Just 15-20 minutes of coloring can help kids decompress after school and improve their ability to focus on homework afterward!
                </p>
              </div>
            </section>

            {/* More Characters */}
            <section id="more-characters" className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                More PAW Patrol Characters 🐾
              </h2>
              <p className="text-slate-600 mb-6">
                Love Skye? Your little one probably loves the whole PAW Patrol team! Here are the other pups you can find coloring pages for:
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { name: "Chase", color: "bg-blue-100 text-blue-700", desc: "Police pup" },
                  { name: "Marshall", color: "bg-red-100 text-red-700", desc: "Fire pup" },
                  { name: "Rubble", color: "bg-yellow-100 text-yellow-700", desc: "Construction pup" },
                  { name: "Rocky", color: "bg-green-100 text-green-700", desc: "Recycling pup" },
                  { name: "Zuma", color: "bg-orange-100 text-orange-700", desc: "Water rescue pup" },
                  { name: "Everest", color: "bg-teal-100 text-teal-700", desc: "Snow rescue pup" }
                ].map((pup, i) => (
                  <div key={i} className={`${pup.color} rounded-xl p-3 text-center`}>
                    <p className="font-semibold">{pup.name}</p>
                    <p className="text-xs opacity-75">{pup.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-gradient-to-r from-violet-500 to-indigo-500 rounded-xl p-5 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5" />
                  <span className="font-semibold">Create Custom PAW Patrol Pages!</span>
                </div>
                <p className="text-violet-100 mb-4 text-sm">
                  Want a specific scene? Use ColorWonder&apos;s AI generator to create custom PAW Patrol coloring pages with any character doing any activity!
                </p>
                <Button
                  onClick={() => window.location.href = "/signup"}
                  className="h-9 px-5 rounded-full bg-white text-violet-600 font-semibold text-sm hover:bg-violet-50"
                >
                  Create Custom Pages →
                </Button>
              </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Parent FAQ ❓
              </h2>
              
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-slate-100 p-5">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="w-5 h-5 text-violet-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold text-slate-800 mb-2">{faq.question}</h4>
                        <p className="text-slate-600 text-sm">{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Final CTA */}
            <div className="bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl p-8 text-white text-center">
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Ready to Color? 🎨
              </h3>
              <p className="text-pink-100 mb-6">
                Download all 7 Skye coloring pages now and watch your little one&apos;s creativity take flight!
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button
                  onClick={() => window.open(pdfUrl, '_blank')}
                  className="h-11 px-8 rounded-full bg-white text-pink-600 font-semibold hover:bg-pink-50"
                  data-testid="download-pdf-bottom"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Free PDF
                </Button>
                <Button
                  onClick={() => window.location.href = "/signup"}
                  variant="outline"
                  className="h-11 px-8 rounded-full border-white text-white font-semibold hover:bg-white/10"
                >
                  Create More Pages →
                </Button>
              </div>
            </div>

            {/* Related Articles */}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-4">You Might Also Like</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/blog/custom-coloring-pages" className="bg-white rounded-xl border border-slate-100 p-4 hover:border-violet-300 transition-colors">
                  <h4 className="font-medium text-slate-800 mb-1">Custom Coloring Pages</h4>
                  <p className="text-slate-500 text-sm">Create personalized coloring pages with any theme</p>
                </Link>
                <Link to="/blog/best-coloring-page-prompts" className="bg-white rounded-xl border border-slate-100 p-4 hover:border-violet-300 transition-colors">
                  <h4 className="font-medium text-slate-800 mb-1">50 Best Coloring Page Prompts</h4>
                  <p className="text-slate-500 text-sm">Get inspired with our top prompt ideas</p>
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Footer */}
        <footer className="border-t border-slate-200 py-8 px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center text-slate-500 text-sm">
            <p>© 2025 ColorWonder. Made with 💜 for parents and kids everywhere.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ArticlePawPatrolSkye;
